import React, { useContext, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { PlusLg } from 'react-bootstrap-icons'
import AddProjectAssetModal from './AddProjectAssetModal'
import AssetListItem from './AssetListItem'
import axiosService from '../../../../../../utils/AxiosService'
import { useParams } from 'react-router-dom'
import { toastError, toastSuccess } from '../../../../../FormComponents/Toasts'
import { Draggable, Droppable } from 'react-beautiful-dnd'
import { CampaignContext } from '../../../../../../contexts/CampaignContext'
import { TimelineContext } from '../../../../../../contexts/TimelineContext'
import { groupProjectAssetsByPhase } from '../../../../../../utils/projectAssetPhases'
import ProjectAssetPhaseGroup from '../../../../../Shared/TimelineParts/ProjectAssetPhaseGroup'
import { useReadOnly } from '../../../../../../contexts/ReadOnlyContext'

export default observer(function ProjectAssets() {
  const { id } = useParams()
  const readOnly = useReadOnly()
  const { campaignState, projectAssets } = useContext(CampaignContext)
  const { timelineState } = useContext(TimelineContext)
  const [modalOpen, setModalOpen] = useState(false)

  const phases = groupProjectAssetsByPhase(projectAssets)
  let dragIndex = 0
  const phasesWithDragIndex = phases.map((phase) => ({
    ...phase,
    assets: phase.assets.map((asset) => {
      const index = dragIndex
      dragIndex += 1
      return { asset, dragIndex: index }
    }),
  }))
  const isAssetUsedInTimeline = (assetId) => timelineState.isAssetUsedInTimeline(assetId)

  return (
    <div className="mt-5 w-full min-w-0">
      <div className='flex items-center justify-between'>
        <h2 className='font-bold text-base dark:text-white'>Project assets</h2>
        <span className='text-rb-lighter-grey dark:text-gray-400'>{projectAssets?.length}</span>
      </div>
      {!readOnly && (
        <button
          onClick={() => setModalOpen(true)}
          className="mt-2 flex w-full items-center justify-between rounded-lg border-2 border-dashed border-rb-dash-border bg-gray-100 px-2 py-1 text-rb-black transition hover:bg-white dark:border-rb-dark-mode-lighter/70 dark:bg-rb-dark-mode-lighter/50 dark:text-white dark:hover:bg-rb-dark-mode-lighter"
        >
          <span className="text-xs font-semibold">Create new asset</span>
          <PlusLg />
        </button>
      )}

      <Droppable
        droppableId="project-assets"
        isDropDisabled={true}
        type="timelineItem"
      >
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="mt-2 min-h-[50px] w-full min-w-0"
          >
            {phasesWithDragIndex.map((phase) => (
              <ProjectAssetPhaseGroup
                key={phase.id}
                phaseId={phase.id}
                label={phase.label}
                count={phase.assets.length}
              >
                {phase.assets.map(({ asset, dragIndex }) => (
                    <Draggable
                      key={`project-asset-${asset.id}`}
                      draggableId={`project-asset-${asset.id}`}
                      index={dragIndex}
                      isDragDisabled={readOnly || isAssetUsedInTimeline(asset.id)}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="w-full min-w-0"
                          style={{
                            ...provided.draggableProps.style,
                            opacity: snapshot.isDragging ? 0.7 : 1,
                            cursor: readOnly ? 'pointer' : isAssetUsedInTimeline(asset.id) ? 'not-allowed' : 'grab',
                          }}
                        >
                          <AssetListItem
                            asset={asset}
                            isUsedInTimeline={isAssetUsedInTimeline(asset.id)}
                            onDelete={() =>
                              axiosService
                                .delete(`/api/project_manager/campaigns/${id}/project_assets/${asset.id}`)
                                .then((removedAsset) => {
                                  campaignState.projectAssets = projectAssets.filter(
                                    (a) => a.id !== removedAsset.id,
                                  )
                                  toastSuccess('Project asset deleted')
                                })
                            }
                          />
                        </div>
                      )}
                    </Draggable>
                ))}
              </ProjectAssetPhaseGroup>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>

      <AddProjectAssetModal modalOpen={modalOpen} setModalOpen={setModalOpen} />
    </div>
  )
})
